/**
 * Helper pour gérer les dates dynamiques dans les URLs partagées
 * Supporte les tokens @today et -Xd pour des vues temporelles dynamiques
 */

/**
 * Convertit une URL avec dates absolues en format dynamique si applicable
 * Si endTime >= aujourd'hui (00:00), remplace par @today et calcule l'intervalle
 * 
 * NOTE IMPORTANTE : Si endTime est dans le futur, l'intervalle est calculé 
 * depuis startTime jusqu'à AUJOURD'HUI (pas jusqu'à endTime) pour éviter 
 * d'avoir des jours "en trop" lors de la reconversion.
 * 
 * @param {string} url - URL complète avec query params
 * @returns {object} { url: string, isDynamic: boolean }
 * 
 * Exemples:
 *   Input:  "/historic?startTime=2026-03-06T00:00:00.000Z&endTime=2026-03-12T23:59:59.999Z"
 *   Output: { url: "/historic?startTime=-6d&endTime=@today", isDynamic: true }
 *   
 *   Input:  "/historic?startTime=2026-03-06T00:00:00.000Z&endTime=2026-03-15T23:59:59.999Z"
 *           (avec aujourd'hui = 2026-03-12)
 *   Output: { url: "/historic?startTime=-6d&endTime=@today", isDynamic: true }
 *           (intervalle calculé jusqu'à aujourd'hui, pas jusqu'au 15)
 */
export function convertUrlToDynamic(url) {
    try {
        const [pathname, search] = url.split('?');
        if (!search) {
            return { url, isDynamic: false };
        }

        const params = new URLSearchParams(search);
        const startTimeStr = params.get('startTime');
        const endTimeStr = params.get('endTime');

        // Si pas de dates ou seulement une seule, pas de conversion
        if (!startTimeStr || !endTimeStr) {
            return { url, isDynamic: false };
        }

        // Parser les dates
        const startTime = new Date(startTimeStr);
        const endTime = new Date(endTimeStr);

        // Vérifier validité
        if (isNaN(startTime.getTime()) || isNaN(endTime.getTime())) {
            console.warn('[dateParser] Invalid date format, skipping conversion');
            return { url, isDynamic: false };
        }

        // Obtenir aujourd'hui à 00:00:00 en timezone locale
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // Si endTime < aujourd'hui, c'est une date passée → pas de conversion
        if (endTime < today) {
            return { url, isDynamic: false };
        }

        // Calculer l'intervalle en jours
        // Important : Si endTime est dans le futur, on calcule l'intervalle jusqu'à aujourd'hui 
        // pour éviter d'avoir des jours "en trop" dans le passé
        const referenceEndDate = endTime > today ? today : endTime;
        const intervalMs = referenceEndDate - startTime;
        const intervalDays = Math.ceil(intervalMs / (1000 * 60 * 60 * 24));

        // Remplacer les dates par les tokens dynamiques
        params.set('startTime', `-${intervalDays}d`);
        params.set('endTime', '@today');

        const dynamicUrl = `${pathname}?${params.toString()}`;
        return { url: dynamicUrl, isDynamic: true };

    } catch (error) {
        console.error('[dateParser] Error converting to dynamic:', error);
        return { url, isDynamic: false };
    }
}

/**
 * Convertit une URL avec tokens dynamiques en dates absolues
 * @today → Date du jour à 23:59:59.999
 * -Xd → Date du jour - X jours à 00:00:00.000
 * 
 * @param {string} url - URL avec potentiels tokens dynamiques
 * @returns {string} URL avec dates absolues
 * 
 * Exemple:
 *   Input:  "/historic?startTime=-6d&endTime=@today"
 *   Output: "/historic?startTime=2026-03-06T00:00:00.000Z&endTime=2026-03-12T23:59:59.999Z"
 */
export function convertUrlToAbsolute(url) {
    try {
        const [pathname, search] = url.split('?');
        if (!search) {
            return url;
        }

        const params = new URLSearchParams(search);
        const startTimeStr = params.get('startTime');
        const endTimeStr = params.get('endTime');

        let hasConversion = false;

        // Traiter endTime
        if (endTimeStr === '@today') {
            const today = new Date();
            today.setHours(23, 59, 59, 999);
            params.set('endTime', today.toISOString());
            hasConversion = true;
        }

        // Traiter startTime (format -Xd)
        if (startTimeStr && startTimeStr.match(/^-\d+d$/)) {
            const days = parseInt(startTimeStr.replace('-', '').replace('d', ''), 10);
            const date = new Date();
            date.setDate(date.getDate() - days);
            date.setHours(0, 0, 0, 0);
            params.set('startTime', date.toISOString());
            hasConversion = true;
        }

        if (!hasConversion) {
            return url; // Pas de tokens détectés, retourner URL originale
        }

        return `${pathname}?${params.toString()}`;

    } catch (error) {
        console.error('[dateParser] Error converting to absolute:', error);
        return url; // Fallback sur URL originale
    }
}

/**
 * Génère une preview des dates calculées pour l'UI (tooltip)
 * 
 * @param {string} url - URL avec tokens dynamiques
 * @returns {string} Preview lisible (ex: "06/03/2026 au 12/03/2026")
 */
export function getDatePreview(url) {
    try {
        const absoluteUrl = convertUrlToAbsolute(url);
        const [, search] = absoluteUrl.split('?');
        
        if (!search) return null;

        const params = new URLSearchParams(search);
        const startTimeStr = params.get('startTime');
        const endTimeStr = params.get('endTime');

        if (!startTimeStr || !endTimeStr) return null;

        const startDate = new Date(startTimeStr);
        const endDate = new Date(endTimeStr);

        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return null;

        const format = (date) => date.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });

        return `${format(startDate)} au ${format(endDate)}`;

    } catch (error) {
        console.error('[dateParser] Error generating preview:', error);
        return null;
    }
}
